/**
 * Public API client for the Sendoc site.
 *
 * Contract (implemented by the backend functions):
 *   POST /api/view
 *     -> { ok: true, document: { title, page_count },
 *          pages: [{ page_number, width, height, url, hd_url?, hd_width?, hd_height?, text? }],
 *          visit_id, visit_token, mode: 'test' | 'live' }
 *   POST /api/view-events
 *     -> { ok: true }
 *   POST /api/access-request
 *     -> { ok: true } | { ok: false, error }
 */

export interface ViewPage {
  page_number: number;
  width: number;
  height: number;
  /** Standard image (~1600 px longest edge). */
  url: string;
  /** HD image (~3200 px) for hi-dpi screens and zooming; absent before the
   *  document has been re-rendered with HD. */
  hd_url?: string;
  hd_width?: number;
  hd_height?: number;
  text?: string;
}

export interface ViewSession {
  ok: true;
  document: { title: string; page_count: number };
  pages: ViewPage[];
  visit_id: string;
  visit_token: string;
  mode: 'test' | 'live';
}

export interface StoredVisit {
  visit_id: string;
  visit_token: string;
}

export type ViewResult =
  | { status: 'ok'; session: ViewSession }
  /** Link revoked, expired, or unknown. */
  | { status: 'revoked' }
  /** Stored visit credentials rejected — caller should start a fresh visit. */
  | { status: 'unauthorized' }
  | { status: 'error'; message: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Validate the /api/view payload; returns a message on contract mismatch. */
export function validateViewSession(data: unknown): ViewSession | string {
  if (!isRecord(data) || data.ok !== true) {
    return isRecord(data) && typeof data.error === 'string'
      ? data.error
      : 'Unexpected response from server.';
  }
  const doc = data.document;
  if (
    !isRecord(doc) ||
    typeof doc.title !== 'string' ||
    typeof doc.page_count !== 'number'
  ) {
    return 'Malformed document payload.';
  }
  if (typeof data.visit_id !== 'string' || typeof data.visit_token !== 'string') {
    return 'Missing visit credentials.';
  }
  if (data.mode !== 'test' && data.mode !== 'live') {
    return 'Unknown session mode.';
  }
  if (!Array.isArray(data.pages) || data.pages.length === 0) {
    return 'Document has no pages.';
  }
  for (const page of data.pages) {
    if (
      !isRecord(page) ||
      typeof page.page_number !== 'number' ||
      typeof page.width !== 'number' ||
      typeof page.height !== 'number' ||
      typeof page.url !== 'string' ||
      (page.hd_url !== undefined && typeof page.hd_url !== 'string') ||
      (page.hd_width !== undefined && typeof page.hd_width !== 'number') ||
      (page.hd_height !== undefined && typeof page.hd_height !== 'number') ||
      (page.text !== undefined && typeof page.text !== 'string')
    ) {
      return 'Malformed page payload.';
    }
  }
  return data as unknown as ViewSession;
}

export async function fetchView(
  shareId: string,
  visit?: StoredVisit | null,
): Promise<ViewResult> {
  let res: Response;
  try {
    res = await fetch('/api/view', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        share_id: shareId,
        visit_id: visit?.visit_id,
        visit_token: visit?.visit_token,
      }),
    });
  } catch {
    return { status: 'error', message: 'Network unavailable. Check your connection and retry.' };
  }
  if (res.status === 404 || res.status === 410) return { status: 'revoked' };
  if (res.status === 401 || res.status === 403) return { status: 'unauthorized' };
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    return { status: 'error', message: `Unreadable response (${res.status}).` };
  }
  if (!res.ok) {
    const message =
      isRecord(data) && typeof data.error === 'string'
        ? data.error
        : `Request failed (${res.status}).`;
    return { status: 'error', message };
  }
  const session = validateViewSession(data);
  if (typeof session === 'string') {
    // Contract mismatch — surface it loudly in development builds.
    console.warn('[sendoc] /api/view contract mismatch:', session, data);
    return { status: 'error', message: session };
  }
  return { status: 'ok', session };
}

export type AccessRequestResult = { ok: true } | { ok: false; error: string };

export async function requestAccess(input: {
  name: string;
  email: string;
  company: string;
  website?: string; // honeypot
}): Promise<AccessRequestResult> {
  let res: Response;
  try {
    res = await fetch('/api/access-request', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, error: 'Network unavailable. Check your connection and try again.' };
  }
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* fall through */
  }
  if (res.ok && isRecord(data) && data.ok === true) return { ok: true };
  const error =
    isRecord(data) && typeof data.error === 'string'
      ? data.error
      : `Request failed (${res.status}). Please try again.`;
  return { ok: false, error };
}

export interface ViewEventPayload {
  share_id: string;
  visit_id: string;
  visit_token: string;
  sequence: number;
  page_number: number;
  active_ms: number;
  event: 'visible' | 'heartbeat' | 'page' | 'hidden' | 'exit';
}

export type SendResult =
  | 'ok'
  /** Transient failure — safe to retry with the identical payload. */
  | 'failed'
  /** The visit token was rejected (401/403) — the visit must be renewed. */
  | 'expired'
  /** The share was revoked or deleted (404/410) — terminal. */
  | 'revoked'
  /** The document became unavailable (409) — terminal for this session. */
  | 'unavailable';

/**
 * Deliver one tracking event. Never throws.
 * - 'ok' when accepted (or, for beacons, successfully queued),
 * - 'expired' when the server rejects the visit credentials (401/403),
 * - 'revoked' when the share is gone (404/410),
 * - 'unavailable' when the document is not ready (409),
 * - 'failed' for anything else (caller may retry with the same payload).
 */
export async function sendViewEvent(
  payload: ViewEventPayload,
  opts: { keepalive: boolean },
): Promise<SendResult> {
  const body = JSON.stringify(payload);
  try {
    const res = await fetch('/api/view-events', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: opts.keepalive,
    });
    if (res.ok) return 'ok';
    if (res.status === 401 || res.status === 403) return 'expired';
    if (res.status === 404 || res.status === 410) return 'revoked';
    if (res.status === 409) return 'unavailable';
    return 'failed';
  } catch {
    // Network gone (or fetch keepalive unsupported): for page-unload events,
    // fall back to the beacon queue as a best-effort delivery.
    if (opts.keepalive && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        const queued = navigator.sendBeacon(
          '/api/view-events',
          new Blob([body], { type: 'application/json' }),
        );
        if (queued) return 'ok';
      } catch {
        /* nothing left to try */
      }
    }
    return 'failed';
  }
}
